import React, { useCallback, useEffect, useState } from "react";
import pinPrompt from "../utils/pinPrompt";
import { loadCutSheetSwitchOptions, switchCutSheet, switchSummary, hoursText } from "../features/jobs/cutSheetSwitch";
import DuplicateCutSheetForm from "./DuplicateCutSheetForm.jsx";

/**
 * Switch a pending laser job to another cut sheet (2026-10-21): the same nest on substitute steel when the
 * ordered material is late (SO-1144, 10/21: Ryerson's 1/2" HRPO late, Boyd's HR in). The task and its job's
 * parts move to the twin; both materials' reservations are rewritten by JobQ. A manager's PIN (Admin or
 * Manager, typed every time) + a reason.
 *
 * Lists what GET /tasks/{id}/switch_cut_sheet offers (the twins: same parts at the same quantity per sheet),
 * each with its steel (on hand, reserved, on order, what the order already holds earmarked) and its own
 * refusal / warnings. No twin yet: "Copy onto another material" makes one (DuplicateCutSheetForm) and
 * picks it. `onSwitched(out)` runs when the dialog CLOSES after a switch, with the API's answer: a page that
 * reloads at once can swap itself for a spinner, which unmounted the dialog and its result, and it came back
 * offering the switch back (Mike on dev, 10/21: two "test change" switches, #1220 -> #1486 -> #1220).
 */
export default function CutSheetSwitchModal({ show, taskId, onHide, onSwitched }) {
  const [opts, setOpts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [recompute, setRecompute] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copying, setCopying] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(async (pick = null) => {
    if (!taskId) return;
    setLoading(true);
    setError("");
    try {
      const o = await loadCutSheetSwitchOptions(taskId);
      setOpts(o);
      const eligible = (o.candidates || []).filter((c) => !c.refusal);
      const want = pick != null ? eligible.find((c) => c.cut_sheet_id === pick) : null;
      setPicked(want ? want.cut_sheet_id : eligible.length === 1 ? eligible[0].cut_sheet_id : null);
    } catch (e) {
      setError(e.message || "Could not load this task's cut sheets.");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (!show) {
      setOpts(null); setError(""); setPicked(null); setReason(""); setRecompute(false);
      setBusy(false); setResult(null); setCopying(false); setNotice("");
      return;
    }
    load();
  }, [show, load]);

  if (!show) return null;
  const close = () => {
    if (result && onSwitched) onSwitched(result);
    onHide();
  };
  const task = opts?.task;
  const cands = opts?.candidates || [];
  const pickedRow = cands.find((c) => c.cut_sheet_id === picked) || null;
  const reasonOk = reason.trim().length >= 5;

  const doSwitch = async () => {
    if (!pickedRow || !reasonOk || !task) return;
    const pin = await pinPrompt(
      `Manager PIN to switch Job #${task.master_job_num} from ${task.cut_sheet_name} to ${pickedRow.cut_sheet_name}`,
      { autofill: false });
    if (!pin) return;
    setBusy(true);
    setError("");
    const r = await switchCutSheet(task.task_id, {
      toCutSheetId: pickedRow.cut_sheet_id, fromCutSheetId: task.cut_sheet_id, reason: reason.trim(), pin,
      recomputeEstimate: recompute,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setResult(r.data);
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} role="dialog"
      aria-label="Switch cut sheet">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title">
              Switch cut sheet{task ? <> · Job #{task.master_job_num}{task.order_number ? ` · ${task.order_number}` : ""}</> : null}
            </h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={close} disabled={busy}></button>
          </div>
          <div className="modal-body">
            {loading && !opts && <div className="text-muted">Loading…</div>}
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {result ? (
              <div className="alert alert-success" data-testid="switch-result">
                <div>{switchSummary(result)}</div>
                <div className="small mt-1">
                  Laser estimate {result.estimate_recomputed ? "now" : "kept at"} {hoursText(result.estimated_hours)} ·
                  switched by {result.switched_by}
                </div>
                {(result.warnings || []).map((w) => <div key={w} className="small text-warning-emphasis mt-1">⚠ {w}</div>)}
                <div className="small text-muted mt-1">
                  A traveler printed before now still names {result.from?.cut_sheet_name}: reprint it for the laser.
                </div>
              </div>
            ) : task ? (
              <>
                <div className="mb-2">
                  Task #{task.task_id} cuts <span className="font-monospace fw-semibold">{task.cut_sheet_name}</span>{" "}
                  (#{task.cut_sheet_id}) from <span className="font-monospace">{task.material_code}</span> ·{" "}
                  {task.sheets} sheet{task.sheets === 1 ? "" : "s"} · laser estimate {hoursText(task.estimated_hours)}
                </div>
                {opts.refusal && <div className="alert alert-warning" role="alert">{opts.refusal}</div>}
                {notice && <div className="alert alert-info py-2 small">{notice}</div>}
                {cands.length === 0 ? (
                  <div className="text-muted mb-2">
                    No other cut sheet nests these parts at the same quantity per sheet.
                  </div>
                ) : (
                  <table className="table table-sm align-middle mb-2">
                    <thead>
                      <tr>
                        <th></th><th>Cut sheet</th><th>Steel</th>
                        <th className="text-end">On hand</th><th className="text-end">Reserved</th>
                        <th className="text-end">On order</th><th className="text-end">Est.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cands.map((c) => {
                        const off = !!c.refusal || !!opts.refusal;
                        return (
                          <React.Fragment key={c.cut_sheet_id}>
                            <tr className={c.cut_sheet_id === picked ? "table-primary" : off ? "text-muted" : ""}>
                              <td>
                                <input type="radio" name="switchTarget" className="form-check-input"
                                  aria-label={`Switch to ${c.cut_sheet_name}`} disabled={off}
                                  checked={c.cut_sheet_id === picked} onChange={() => setPicked(c.cut_sheet_id)} />
                              </td>
                              <td>
                                <span className="font-monospace fw-semibold">{c.cut_sheet_name}</span>{" "}
                                <span className="text-muted small">#{c.cut_sheet_id} Rev {c.revision_code || "A"}</span>
                                {c.is_temporary && <span className="badge bg-secondary ms-1">temporary</span>}
                              </td>
                              <td>
                                <span className="font-monospace">{c.material_code}</span>
                                {c.same_material && <span className="badge bg-light text-dark border ms-1">same steel</span>}
                                <div className="small text-muted">{c.material_description}{c.owner_name ? ` · ${c.owner_name}` : ""}</div>
                              </td>
                              <td className="text-end">{c.stock_quantity}</td>
                              <td className="text-end">
                                {c.reserved_quantity}
                                {c.order_earmarked > 0 && (
                                  <div className="small text-success">{c.order_earmarked} earmarked for {task.order_number || "this order"}</div>
                                )}
                                {c.other_earmarked > 0 && (
                                  <div className="small text-danger">{c.other_earmarked} held for other orders</div>
                                )}
                              </td>
                              <td className="text-end">{c.inbound}</td>
                              <td className="text-end">{hoursText(c.est_hours)}</td>
                            </tr>
                            {(c.refusal || (c.warnings || []).length > 0) && (
                              <tr className="border-0">
                                <td></td>
                                <td colSpan={6} className="pt-0 small">
                                  {c.refusal && <div className="text-danger">{c.refusal}</div>}
                                  {!c.refusal && (c.warnings || []).map((w) => <div key={w} className="text-warning-emphasis">⚠ {w}</div>)}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {!opts.refusal && (
                  copying ? (
                    <div className="mb-3">
                      <DuplicateCutSheetForm cutSheetId={task.cut_sheet_id} cutSheetName={task.cut_sheet_name}
                        materialId={task.material_id} onCancel={() => setCopying(false)}
                        onCreated={(d) => {
                          setCopying(false);
                          setNotice(`Made ${d.cut_sheet_name} (#${d.cut_sheet_id}) on ${d.material_code}.`
                            + ((d.warnings || []).length ? ` ${d.warnings.join(" ")}` : ""));
                          load(d.cut_sheet_id);
                        }} />
                    </div>
                  ) : (
                    <div className="mb-2">
                      <button type="button" className="btn btn-link btn-sm px-0" onClick={() => setCopying(true)}>
                        Copy {task.cut_sheet_name} onto another material…
                      </button>
                    </div>
                  )
                )}
                {!opts.refusal && cands.some((c) => !c.refusal) && (
                  <>
                    <label className="form-label small mb-0 d-block" htmlFor="switchReason">Why (at least 5 characters)</label>
                    <input id="switchReason" className="form-control form-control-sm mb-2" value={reason}
                      placeholder="e.g. Ryerson HRPO late (PO 1398); Boyd HR in (PO 1406)"
                      onChange={(e) => setReason(e.target.value)} />
                    <div className="form-check mb-1">
                      <input className="form-check-input" type="checkbox" id="switchRecompute" checked={recompute}
                        onChange={(e) => setRecompute(e.target.checked)} />
                      <label className="form-check-label small" htmlFor="switchRecompute">
                        Take the laser estimate from the new sheet{pickedRow ? ` (${hoursText(pickedRow.est_hours)})` : ""}{" "}
                        instead of keeping {hoursText(task.estimated_hours)} (keep it when it is the same Amada program)
                      </label>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
          <div className="modal-footer">
            {result ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={close}>Done</button>
            ) : (
              <>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onHide} disabled={busy}>Cancel</button>
                <button type="button" className="btn btn-warning btn-sm" onClick={doSwitch}
                  disabled={busy || !pickedRow || !reasonOk || !!opts?.refusal}>
                  {busy ? "Switching…" : pickedRow ? `Switch to ${pickedRow.cut_sheet_name}` : "Switch"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
